import { makeQueue } from './queue';

export const EVENTS = {
    jobApplySuccess: 'job_apply_success',
    funnel: 'funnel',
    userdata: 'userdata',
    trackVisibility: 'show_element',
    abTest: 'ab_test',
    pageView: 'pageview',
};

export const FUNNEL_TYPES = {
    application: 'Application',
    cvExtraction: 'CV Extraction Application Form',
};

export const FUNNEL_STEP = {
    loginWall: 'Registration/Login Step',
    loginSuccess: 'Login Success',
    registerSuccess: 'Register Success',
    form: 'Form',
    customFields: 'Custom Fields',
    success: 'Success',
    cvExtractionShowModal: 'CV Modal Shown',
    cvExtractionFileUploadSuccess: 'CV File Upload',
    cvExtractionConsent: 'Consent Given',
    cvExtractionSuccess: 'CV Extraction Completed',
};

export const FUNNEL_VARIANTS = {
    default: 'Default',
    customFields: 'Custom Fields',
};

/**
 * Tracker
 * @typedef {object} Tracker
 * @property {function} enable
 * @property {function} event
 * @property {function} [trackUser]
 * @property {function} [trackVisibility]
 * @property {function} [trackABTest]
 * @property {function} [trackVirtualPageView]
 */

/** @type {Tracker[]} */
const trackers = [];

/**
 * Register tracker.
 *
 * @param {Tracker} tracker
 */
export function registerTracker(tracker) {
    trackers.push(tracker);
}

const eventQueue = makeQueue();

/**
 * Enable trackers.
 *
 * @param {array} dependencies
 */
export function enableTrackers(dependencies) {
    eventQueue.add(() => {
        trackers.forEach((tracker, index) => tracker.enable?.(dependencies[index]));
    });
}

/**
 * Send user data for every tracker.
 */
export function trackUser() {
    eventQueue.add(() => Promise.all(trackers.map(tracker => tracker.trackUser?.())));
}

interface TrackingEvent {
    event?: string;
    element?: string;
    elementDetail?: string;
    jobId?: number;
    jobType?: 'SJA' | 'Standard';
    productType?: string;
    applicationType?: 'SmartBewerben';
    userData?: {
        email?: string | null;
    };
    funnelType?: string;
    funnelStep?: string;
    funnelVariant?: string;
}

export function track(event: Partial<TrackingEvent>) {
    eventQueue.add(() => {
        trackers.forEach(tracker => tracker.event(event));
    });
}

/**
 * Send show_element event for every tracker.
 */
export function trackVisibility({ element, elementDetail }) {
    eventQueue.add(() => {
        trackers.forEach(tracker => tracker.trackVisibility?.({ element, elementDetail }));
    });
}

/**
 * Send ab_test event for every tracker.
 */
export function trackABTest({ name, variant }) {
    eventQueue.add(() => {
        trackers.forEach(tracker => tracker.trackABTest?.({ name, variant }));
    });
}

/**
 * Send page_view event for every tracker.
 */
export function trackVirtualPageView({ pageUrl, pageTitle }) {
    eventQueue.add(() => {
        trackers.forEach(tracker => tracker.trackVirtualPageView?.({ pageUrl, pageTitle }));
    });
}
